import { useEffect, useMemo, useState } from "react";

import { listPermissions } from "./permissions.gateway";
import type { Permission } from "./permissions.types";

export const PERMISSION_OPTIONS_PAGE_SIZE = 100;
const MAX_PERMISSION_PAGES = 100;

export interface PermissionOption {
  value: string;
  label: string;
}

export interface UsePermissionOptionsResult {
  options: PermissionOption[];
  /**
   * Catálogo completo por trás das opções. Diferente de `options`, preserva o
   * código e a descrição de cada permissão, exigidos pela relação selecionável
   * dos pop-ups de perfil.
   */
  permissions: Permission[];
  isLoading: boolean;
  hasError: boolean;
}

function toOption(permission: Permission): PermissionOption {
  return { value: permission.id, label: permission.nome || permission.codigo };
}

async function fetchAllPermissions(signal: AbortSignal): Promise<Permission[]> {
  const collected: Permission[] = [];

  for (let pagina = 1; pagina <= MAX_PERMISSION_PAGES; pagina += 1) {
    const { permissions, total } = await listPermissions({
      pagina,
      tamanhoPagina: PERMISSION_OPTIONS_PAGE_SIZE,
      signal,
    });

    collected.push(...permissions);

    if (permissions.length < PERMISSION_OPTIONS_PAGE_SIZE || collected.length >= total) break;
  }

  return collected;
}

export function usePermissionOptions(): UsePermissionOptionsResult {
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    fetchAllPermissions(controller.signal)
      .then((collected) => {
        if (cancelled) return;
        setPermissions(collected);
        setHasError(false);
        setIsLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setPermissions([]);
        setHasError(true);
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  const options = useMemo(() => permissions.map(toOption), [permissions]);

  return { options, permissions, isLoading, hasError };
}
